import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Code, Smartphone, Server, Palette, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';
import { projectsData as fallbackProjects } from '../data/mock';

const iconMap = {
  code: Code,
  smartphone: Smartphone,
  server: Server,
  palette: Palette
};

/** Normalize external project URL — reject relative / broken values. */
function projectHref(raw) {
  if (typeof raw !== 'string') return null;
  const link = raw.trim();
  if (!/^https?:\/\/[^\s]+$/i.test(link)) return null;
  try {
    const u = new URL(link);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u.toString();
  } catch {
    return null;
  }
}

const ProjectCard = ({ project, index }) => {
  const { language } = useLanguage();
  const [isHovered, setIsHovered] = useState(false);
  const href = projectHref(project.link);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="group relative h-full"
    >
      <motion.div
        whileHover={{ y: -8 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="relative h-full bg-gradient-to-br from-[#1a1a1b] to-[#0f0f10] rounded-2xl overflow-hidden border border-cyan-500/10 hover:border-cyan-500/30 transition-all duration-500"
      >
        <motion.div
          aria-hidden
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{
            background: 'radial-gradient(circle at 50% 0%, rgba(0, 217, 255, 0.1), transparent 70%)'
          }}
        />

        <div className="relative h-64 overflow-hidden pointer-events-none">
          <motion.img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover"
            animate={{
              scale: isHovered ? 1.1 : 1
            }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f10] via-transparent to-transparent" />

          <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-2">
            {(project.tech || []).slice(0, 3).map((tech, i) => (
              <motion.span
                key={tech}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + i * 0.1 }}
                className="px-3 py-1 text-xs font-mono bg-cyan-500/10 text-cyan-300 rounded-full border border-cyan-500/20 backdrop-blur-sm"
              >
                {tech}
              </motion.span>
            ))}
          </div>
        </div>

        <div className="relative z-10 p-6">
          <div className="flex items-start justify-between gap-3 mb-3 min-w-0">
            <div className="min-w-0 flex-1">
              <h3 className="text-2xl font-bold text-white mb-1 group-hover:text-cyan-400 transition-colors text-balance">
                {project.title}
              </h3>
              <p className="text-sm text-gray-500 font-mono break-words">{project.category} • {project.year}</p>
            </div>
            {project.featured && (
              <span className="shrink-0 px-2 py-1 text-xs font-semibold bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-md">
                Featured
              </span>
            )}
          </div>

          <p className="text-gray-400 leading-relaxed mb-6 text-pretty">
            {project.description?.[language] || project.description?.fr || ''}
          </p>

          <div className="relative z-20 flex gap-3">
            {href ? (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.open(href, '_blank', 'noopener,noreferrer');
                }}
                className="relative z-20 flex-1 px-4 py-2 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 border border-cyan-500/20 cursor-pointer"
              >
                <ExternalLink className="w-4 h-4 shrink-0 pointer-events-none" />
                <span className="pointer-events-none">
                  {language === 'fr' ? 'Voir le projet' : 'View project'}
                </span>
              </a>
            ) : (
              <span className="flex-1 px-4 py-2 text-center text-gray-500 rounded-lg border border-white/5 text-sm">
                {language === 'fr' ? 'Lien à venir' : 'Link coming soon'}
              </span>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const Projects = () => {
  const { language } = useLanguage();
  const [projectsData, setProjectsData] = useState(fallbackProjects);

  useEffect(() => {
    api.getProjects()
      .then((response) => {
        const list = Array.isArray(response.data) ? response.data : [];
        if (list.length > 0) setProjectsData(list);
      })
      .catch(() => {
        setProjectsData(fallbackProjects);
      });
  }, []);

  return (
    <section id="projects" className="relative py-32 bg-[#0a0a0b] overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6"
          >
            <span className="text-sm text-cyan-400 font-medium font-mono">
              {language === 'fr' ? 'RÉALISATIONS' : 'PORTFOLIO'}
            </span>
          </motion.div>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 text-balance hyphens-none px-1">
            {language === 'fr' ? 'Projets' : 'Selected'}{' '}
            <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              {language === 'fr' ? 'Sélectionnés' : 'Projects'}
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto text-pretty px-1">
            {language === 'fr'
              ? 'Une sélection de projets qui illustrent mon expertise en développement full-stack et UX'
              : 'A selection of projects showcasing my expertise in full-stack development and UX'
            }
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {projectsData.map((project, index) => (
            <ProjectCard key={project.id} project={project} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
